import React, { Component } from "react";
import { Button, Header, Icon, Image, Label, Message, Segment } from "semantic-ui-react";
import "./styles/sass/platform-migration.scss";
import CopyIcon from "../media/copy-icon.svg";
import LinkIcon from "../media/link-icon.svg";
import Logo from "../media/newLogo.svg";
import ShieldIcon from "../media/shield-icon.svg";
import SignInIcon from "../media/sign-in-icon.svg";
import SignInGuideModal from "./sign-in-guide-modal";

const REDIRECT_SECONDS = 60;
const ENGINE_URLS = {
  prod: "https://engine.klearnow.com/login",
  stage: "https://engine.stage.klearnow.com/login",
  staging: "https://engine.stage.klearnow.com/login",
  qat: "https://engine.qat.klearnow.com/login",
  dev: "https://engine.dev.klearnow.com/login",
};

const getEngineLoginUrl = () => {
  const env = window._env_.ENVIRONMENT;
  return ENGINE_URLS[env] || ENGINE_URLS.stage;
};

const SIGNIN_OPTIONS = [
  {
    iconSrc: ShieldIcon,
    title: "SSO (Single Sign-On)",
    desc: "New on Unified platform. Activate SSO access once whenever you are ready. Legacy Sign In remains available in the meantime.",
  },
  {
    iconSrc: SignInIcon,
    title: "Legacy Sign In",
    desc: "Use your existing Customs360 email and password. No setup required.",
  },
];

class PlatformMigration extends Component {
  constructor() {
    super();
    this.state = { secondsRemaining: REDIRECT_SECONDS, paused: false, copied: false, guideOpen: false };
    this.timerId = null;
    this.engineLoginUrl = getEngineLoginUrl();
    this.displayUrl = this.engineLoginUrl.replace(/^https?:\/\//, "");
  }

  componentDidMount = () => {
    this.timerId = setInterval(() => {
      this.setState((prev) => {
        if (prev.paused || prev.guideOpen) return null;
        const next = prev.secondsRemaining - 1;
        if (next <= 0) {
          clearInterval(this.timerId);
          window.location.href = this.engineLoginUrl;
          return { secondsRemaining: 0 };
        }
        return { secondsRemaining: next };
      });
    }, 1000);
  };

  componentWillUnmount = () => {
    if (this.timerId) clearInterval(this.timerId);
  };

  copyUrl = () => {
    const { clipboard } = navigator;
    if (clipboard && clipboard.writeText) {
      clipboard.writeText(this.displayUrl);
    } else {
      const el = document.createElement("textarea");
      el.value = this.displayUrl;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
    }
    this.setState({ copied: true });
    setTimeout(() => this.setState({ copied: false }), 2000);
  };

  renderRing = () => {
    const { secondsRemaining } = this.state;
    const r = 18;
    const c = 2 * Math.PI * r;
    return (
      <div className="ring">
        <svg viewBox="0 0 44 44" aria-hidden="true">
          <circle className="bg" cx="22" cy="22" r={r} />
          <circle className="progress" cx="22" cy="22" r={r} strokeDasharray={c} strokeDashoffset={c * (1 - secondsRemaining / REDIRECT_SECONDS)} />
        </svg>
        <span className="number">{secondsRemaining}</span>
      </div>
    );
  };

  render() {
    const { secondsRemaining, paused, copied, guideOpen } = this.state;

    return (
      <div className="platform-migration-page">
        <Segment className="pm-card">
          <div className="pm-card-scroll">
            <div className="pm-header">
              <Image className="pm-logo" src={Logo} alt="KlearNow.AI" />
              <Label className="pm-badge">
                <Icon name="clock outline" /> Platform Update
              </Label>
            </div>

            <Header as="h1" className="pm-title">Customs360 has moved to a unified platform</Header>
            <p className="pm-description">
              Customs360 is now part of Unified platform, a single platform for all your trade and
              compliance needs. Your account, settings, and platform capabilities remain exactly the same.
            </p>

            <div className="pm-url-box">
              <div className="pm-url-icon-wrap">
                <Image src={LinkIcon} alt="" className="pm-url-icon" />
              </div>
              <div className="content">
                <span className="label">New Platform URL</span>
                <span className="url">{this.displayUrl}</span>
              </div>
              <Button basic size="small" onClick={this.copyUrl}>
                <Image src={CopyIcon} alt="" className="pm-copy-icon" /> {copied ? "Copied" : "Copy"}
              </Button>
            </div>

            <Message icon className="pm-tip">
              <Icon name="bookmark" className="pm-tip-icon" />
              <Message.Content>
                <strong>Tip: </strong> Please update your bookmarks to the new URL above. This page will not be available indefinitely.
              </Message.Content>
            </Message>

            <Header as="h2" className="pm-section">How you can Sign In</Header>
            {SIGNIN_OPTIONS.map(({ icon, iconSrc, title, desc }) => (
              <div className="pm-option" key={title}>
                <div className="icon-wrap">
                  {iconSrc ? (
                    <Image src={iconSrc} alt="" className="pm-option-icon" />
                  ) : (
                    <Icon name={icon} />
                  )}
                </div>
                <div>
                  <p className="title">{title}</p>
                  <p className="desc">{desc}</p>
                </div>
              </div>
            ))}

            <button type="button" className="pm-guide" onClick={() => this.setState({ guideOpen: true })}>
              Step by step Sign-in Guide
            </button>
          </div>

          <div className="pm-footer">
            <div className="countdown">
              {this.renderRing()}
              <div>
                <div className="label">{paused ? "Redirecting paused" : "Redirecting automatically"}</div>
                <div className="sub">{secondsRemaining} second{secondsRemaining !== 1 ? "s" : ""} remaining</div>
              </div>
            </div>
            <div className="actions">
              <Button className="outline" onClick={() => this.setState(({ paused }) => ({ paused: !paused }))}>
                <Icon name={paused ? "play" : "pause"} /> {paused ? "Resume" : "Pause"}
              </Button>
              <Button primary className="pm-open" onClick={() => { window.location.href = this.engineLoginUrl; }}>
                Open Unified Platform
              </Button>
            </div>
          </div>
        </Segment>

        <p className="pm-support">
          Need help? Contact <a href="mailto:support@klearnow.com">support@klearnow.com</a>
        </p>

        <SignInGuideModal
          open={guideOpen}
          onClose={() => this.setState({ guideOpen: false })}
          engineLoginUrl={this.engineLoginUrl}
          displayUrl={this.displayUrl}
        />
      </div>
    );
  }
}

export default PlatformMigration;
